/**
 * Auth compatibility shim (file name kept for import stability).
 *
 * Provider: VITE_AUTH_PROVIDER=appwrite | supabase (default: appwrite).
 */

import { supabase } from './supabase';
import {
  clearSupabaseToken,
  getStoredSupabaseToken,
  storeSupabaseToken,
} from './supabaseAuth';
import {
  completeAppwritePasswordRecovery,
  getAppwriteJwtForApi,
  getAppwriteSessionUser,
  getStoredAppwriteJwt,
  isAppwriteJwtFresh,
  refreshAppwriteJwt,
  requestAppwritePasswordRecovery,
  sendAppwriteEmailVerification,
  resendAppwriteEmailVerification,
  signInWithAppwriteEmail,
  signInWithAppwriteApple,
  signInWithAppwriteGoogle,
  signOutAppwrite,
  signUpWithAppwriteEmail,
  type AppwriteCompatUser,
} from './appwriteAuth';
import { submitTrace } from './submitPipelineTrace';

type CompatUser = {
  uid: string;
  email: string | null;
  displayName: string | null;
};

type AuthStateCallback = (user: CompatUser | null) => void;

type TraceLike = {
  start: () => void;
  stop: () => void;
  putMetric: (name: string, value: number) => void;
  putAttribute: (name: string, value: string) => void;
};

const LEGACY_FIREBASE_STORAGE_KEY = 'firebase_token';
const authProvider = (
  import.meta.env.VITE_AUTH_PROVIDER || 'appwrite'
).toLowerCase();

const useAppwrite = authProvider === 'appwrite';

let currentUserCache: CompatUser | null = null;
const authListeners = new Set<AuthStateCallback>();

const notifyAuthListeners = () => {
  for (const listener of authListeners) {
    listener(currentUserCache);
  }
};

const mapSupabaseUser = (user: any | null): CompatUser | null => {
  if (!user) return null;

  return {
    uid: user.id,
    email: user.email ?? null,
    displayName:
      user.user_metadata?.full_name ??
      user.user_metadata?.name ??
      user.email?.split('@')[0] ??
      null,
  };
};

const mapAppwriteUser = (user: AppwriteCompatUser | null): CompatUser | null => {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
  };
};

const persistCompatibilityToken = (token: string | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (token) {
      window.localStorage.setItem(LEGACY_FIREBASE_STORAGE_KEY, token);
      if (!useAppwrite) {
        storeSupabaseToken(token);
      }
    } else {
      window.localStorage.removeItem(LEGACY_FIREBASE_STORAGE_KEY);
      if (!useAppwrite) {
        clearSupabaseToken();
      }
    }
  } catch {
    // Sandboxed iframes can deny localStorage; auth can still use its
    // in-memory Appwrite session for the current request.
  }
};

const primeAppwriteAuth = async () => {
  const user = await getAppwriteSessionUser();
  currentUserCache = mapAppwriteUser(user);
  const jwt = await getAppwriteJwtForApi();
  if (jwt) {
    persistCompatibilityToken(jwt);
  }
  notifyAuthListeners();
};

const primeSupabaseAuth = async () => {
  const { data } = await supabase.auth.getSession();
  currentUserCache = mapSupabaseUser(data.session?.user ?? null);

  const token = data.session?.access_token ?? getStoredSupabaseToken();
  if (token) {
    persistCompatibilityToken(token);
  }
  notifyAuthListeners();
};

if (useAppwrite) {
  void primeAppwriteAuth();
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void primeAppwriteAuth();
      }
    });
  }
} else {
  void primeSupabaseAuth();
  supabase.auth.onAuthStateChange((_event, session) => {
    currentUserCache = mapSupabaseUser(session?.user ?? null);
    persistCompatibilityToken(session?.access_token ?? null);
    notifyAuthListeners();
  });
}

export const auth = {
  get currentUser(): CompatUser | null {
    return currentUserCache;
  },

  onAuthStateChanged(callback: AuthStateCallback) {
    authListeners.add(callback);
    callback(currentUserCache);

    if (useAppwrite) {
      void primeAppwriteAuth().then(() => callback(currentUserCache));
    } else {
      void supabase.auth.getSession().then(({ data }) => {
        const mappedUser = mapSupabaseUser(data.session?.user ?? null);
        currentUserCache = mappedUser;
        callback(mappedUser);
      });

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        const mappedUser = mapSupabaseUser(session?.user ?? null);
        currentUserCache = mappedUser;
        callback(mappedUser);
      });

      return () => {
        authListeners.delete(callback);
        subscription.unsubscribe();
      };
    }

    return () => {
      authListeners.delete(callback);
    };
  },
};

export const initAnalytics = async () => null;

export const createTrace = (_name: string): TraceLike | null => ({
  start() {},
  stop() {},
  putMetric() {},
  putAttribute() {},
});

export const initFirebasePerformance = () => {};

/** Google OAuth only when explicitly enabled (Appwrite provider must be on in console). */
export const isGoogleAuthAvailable = (): boolean => {
  if (!useAppwrite) return true;
  return import.meta.env.VITE_ENABLE_GOOGLE_AUTH === 'true';
};

export const isAppleAuthAvailable = (): boolean => {
  if (!useAppwrite) return true;
  return import.meta.env.VITE_ENABLE_APPLE_AUTH === 'true';
};

export const signInWithGoogle = async (redirectTo?: string): Promise<string> => {
  if (useAppwrite) {
    await signInWithAppwriteGoogle(redirectTo, redirectTo);
    throw new Error('Redirecting to Google…');
  }
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectTo || `${window.location.origin}/dashboard`,
    },
  });
  if (error) {
    throw error;
  }
  throw new Error('Redirecting to Google…');
};

export const signInWithApple = async (redirectTo?: string): Promise<string> => {
  if (useAppwrite) {
    await signInWithAppwriteApple(redirectTo, redirectTo);
    throw new Error('Redirecting to Apple…');
  }
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'apple',
    options: {
      redirectTo: redirectTo || `${window.location.origin}/dashboard`,
    },
  });
  if (error) throw error;
  throw new Error('Redirecting to Apple…');
};

export const requestPasswordReset = async (email: string): Promise<void> => {
  if (useAppwrite) {
    await requestAppwritePasswordRecovery(email);
    return;
  }
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) {
    throw error;
  }
};

export const completePasswordReset = async (
  password: string,
  recovery?: { userId: string; secret: string },
): Promise<void> => {
  if (useAppwrite) {
    if (!recovery?.userId || !recovery?.secret) {
      throw new Error('Invalid recovery link. Request a new reset email.');
    }
    await completeAppwritePasswordRecovery(recovery.userId, recovery.secret, password);
    return;
  }
  const { error } = await supabase.auth.updateUser({ password });
  if (error) {
    throw error;
  }
};

export const resendVerificationEmailWithCredentials = async (
  email: string,
  password: string,
): Promise<void> => {
  if (useAppwrite) {
    await resendAppwriteEmailVerification(email, password);
    return;
  }
  const { error } = await supabase.auth.signInWithPassword({
    email: email.trim(),
    password,
  });
  if (error) {
    throw error;
  }
  await sendEmailVerification();
  await supabase.auth.signOut();
};

export const sendEmailVerification = async (): Promise<void> => {
  if (useAppwrite) {
    await sendAppwriteEmailVerification();
    return;
  }
  const { data } = await supabase.auth.getSession();
  const email = data.session?.user?.email;
  if (!email) {
    throw new Error('Sign in first, then request verification.');
  }
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
  });
  if (error) {
    throw error;
  }
};

export const signInWithEmail = async (email: string, password: string): Promise<string> => {
  if (useAppwrite) {
    const token = await signInWithAppwriteEmail(email, password);
    currentUserCache = mapAppwriteUser(await getAppwriteSessionUser());
    persistCompatibilityToken(token);
    notifyAuthListeners();
    return token;
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw error;
  }

  const token = data.session?.access_token;
  if (!token) {
    throw new Error('Supabase authentication did not return a token');
  }

  currentUserCache = mapSupabaseUser(data.user ?? data.session?.user ?? null);
  persistCompatibilityToken(token);
  return token;
};

export const signUpWithEmail = async (email: string, password: string): Promise<string> => {
  if (useAppwrite) {
    try {
      await signUpWithAppwriteEmail(email, password);
    } catch (error) {
      if (error instanceof Error && error.message === 'CHECK_EMAIL') {
        currentUserCache = null;
        persistCompatibilityToken(null);
        notifyAuthListeners();
        throw error;
      }
      throw error;
    }
    throw new Error('CHECK_EMAIL');
  }

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/login`,
    },
  });

  if (error) {
    throw error;
  }

  const session = data.session ?? (await supabase.auth.getSession()).data.session;
  const token = session?.access_token;
  if (!token) {
    throw new Error('CHECK_EMAIL');
  }

  currentUserCache = mapSupabaseUser(data.user ?? session.user ?? null);
  persistCompatibilityToken(token);
  return token;
};

export const signOutUser = async (): Promise<void> => {
  if (useAppwrite) {
    await signOutAppwrite();
  } else {
    await supabase.auth.signOut();
  }
  currentUserCache = null;
  persistCompatibilityToken(null);
  notifyAuthListeners();
};

export const getCurrentUserToken = async (forceRefresh = false): Promise<string | null> => {
  if (useAppwrite) {
    submitTrace('auth_start');
    const stored = !forceRefresh ? getStoredAppwriteJwt() : null;
    if (stored && isAppwriteJwtFresh(stored)) {
      submitTrace('auth_cache_hit');
      persistCompatibilityToken(stored);
      return stored;
    }
    // Prefer createJWT while session cookie is valid — avoids blocking on account.get().
    if (!forceRefresh) {
      const quickJwt = await getAppwriteJwtForApi(true);
      if (quickJwt) {
        persistCompatibilityToken(quickJwt);
        return quickJwt;
      }
    }
    const sessionUser = await getAppwriteSessionUser();
    if (!sessionUser) {
      persistCompatibilityToken(null);
      submitTrace('auth_fail', 'no session user');
      return null;
    }
    const token = await getAppwriteJwtForApi(forceRefresh);
    if (token) {
      persistCompatibilityToken(token);
    } else {
      submitTrace('auth_fail', 'jwt not issued');
    }
    return token;
  }

  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token ?? getStoredSupabaseToken();
  if (token) {
    persistCompatibilityToken(token);
  }
  return token ?? null;
};

export const getCurrentUser = () => currentUserCache;
